/**
 * Dev mock for post-op OFAC soft-hit (`actionRequired: OFAC_DISAMBIGUATION`).
 * See docs/ofac-disambiguation-contract.md.
 */

import axios from "axios";
import type {
  IOperationActionRequired,
  IOperationResponse,
  IRecipientScreeningRequest,
} from "../services/api";

const FLAG_KEY = "raas.mockOfac";
const OUTCOME_KEY = "raas.mockOfacOutcome";

/** Ops that already cleared soft-hit in this tab session (mock only). */
const clearedOperationIds = new Set<string>();

export type MockOfacOutcome = "clear" | "still_required" | "hit";

const DEFAULT_ACTION: IOperationActionRequired = {
  type: "OFAC_DISAMBIGUATION",
  requiredFields: ["dateOfBirth", "address"],
  attemptId: "mock-attempt-1",
  messageKey: "ofacDisambiguation.helper",
};

/** Sync query `?mockOfac=1|0` into localStorage once per page load. */
export const syncMockOfacFromQuery = (): void => {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("mockOfac");
  if (raw === "1" || raw === "true") {
    localStorage.setItem(FLAG_KEY, "1");
  } else if (raw === "0" || raw === "false") {
    localStorage.removeItem(FLAG_KEY);
  }

  const outcome = params.get("mockOfacOutcome");
  if (
    outcome === "clear" ||
    outcome === "still_required" ||
    outcome === "hit"
  ) {
    localStorage.setItem(OUTCOME_KEY, outcome);
  }
};

export const isMockOfacEnabled = (): boolean => {
  if (typeof window === "undefined") return false;
  syncMockOfacFromQuery();
  return localStorage.getItem(FLAG_KEY) === "1";
};

export const getMockOfacOutcome = (): MockOfacOutcome => {
  if (typeof window === "undefined") return "clear";
  const raw = localStorage.getItem(OUTCOME_KEY);
  if (raw === "still_required" || raw === "hit" || raw === "clear") return raw;
  return "clear";
};

export const setMockOfacOutcome = (outcome: MockOfacOutcome): void => {
  if (typeof window === "undefined") return;
  localStorage.setItem(OUTCOME_KEY, outcome);
};

const isInjectableStatus = (status: string): boolean => {
  const s = status.toLowerCase();
  return s === "processing" || s === "pending" || s === "created";
};

/** Attach OFAC soft-hit payload when mock is on and the op is mid-flight. */
export const injectOfacActionRequired = (
  op: IOperationResponse
): IOperationResponse => {
  if (!isMockOfacEnabled()) return op;
  if (clearedOperationIds.has(op.operationId)) {
    return { ...op, actionRequired: null };
  }
  if (op.actionRequired?.type === "OFAC_DISAMBIGUATION") return op;
  if (!isInjectableStatus(op.status)) return op;
  return {
    ...op,
    status: op.status === "created" ? "processing" : op.status,
    actionRequired: { ...DEFAULT_ACTION },
  };
};

export const injectOfacActionRequiredList = (
  items: IOperationResponse[]
): IOperationResponse[] => {
  if (!isMockOfacEnabled() || items.length === 0) return items;
  let injected = false;
  return items.map((op) => {
    if (clearedOperationIds.has(op.operationId)) {
      return { ...op, actionRequired: null };
    }
    if (injected || !isInjectableStatus(op.status)) return op;
    injected = true;
    return injectOfacActionRequired(op);
  });
};

/** Simulated POST recipient-screening when mock is enabled. */
export const mockSubmitRecipientScreening = async (
  operation: IOperationResponse,
  _body: IRecipientScreeningRequest
): Promise<IOperationResponse> => {
  await new Promise((r) => setTimeout(r, 400));
  const outcome = getMockOfacOutcome();

  if (outcome === "hit") {
    throw new axios.AxiosError(
      "Recipient matched sanctions list",
      "ERR_BAD_RESPONSE",
      undefined,
      undefined,
      {
        status: 403,
        statusText: "Forbidden",
        headers: {},
        config: {} as never,
        data: {
          success: false,
          error: {
            code: "OFAC_HIT",
            message: "Recipient matched sanctions list",
          },
        },
      }
    );
  }

  if (outcome === "still_required") {
    return {
      ...operation,
      status: "processing",
      actionRequired: {
        ...DEFAULT_ACTION,
        attemptId: `mock-attempt-${Date.now()}`,
      },
    };
  }

  clearedOperationIds.add(operation.operationId);
  return {
    ...operation,
    actionRequired: null,
  };
};
